import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

interface UseScrollPinningOptions {
  sentinelRef: RefObject<HTMLElement | null>;
  releaseOffsetRef?: RefObject<number>;
  transitionLockRef?: RefObject<boolean>;
  enabled: boolean;
  pinned?: boolean;
  activateAt: number;
  releaseAt: number;
  schedule?: 'sync' | 'animationFrame';
}

function getNextPinnedState(
  currentPinned: boolean,
  thresholdTop: number,
  activateAt: number,
  releaseAt: number,
) {
  if (currentPinned) {
    return thresholdTop < releaseAt;
  }

  return thresholdTop <= activateAt;
}

/**
 * Derives a sticky "pinned" state from a sentinel element's scroll position.
 *
 * Several navigation surfaces in this codebase need the same core behavior:
 * they should switch into a pinned visual state once a sentinel crosses a
 * threshold, and they should not immediately flap back when layout changes at
 * the exact moment pinning occurs. This hook packages that logic into one
 * reusable primitive.
 *
 * Important details the implementation handles:
 *
 * - hysteresis via separate `activateAt` and `releaseAt` thresholds, so a
 *   pinned element does not oscillate when scroll position hovers near the
 *   boundary
 * - optional `releaseOffsetRef` support so callers can incorporate a measured
 *   release band when the pinned UI changes height after activation
 * - optional `transitionLockRef` support so callers can temporarily freeze the
 *   current state during a layout transition that would otherwise produce a
 *   false unpin
 * - either synchronous or animation-frame scheduled reads, allowing mobile
 *   callers to coalesce scroll work when they are more sensitive to event churn
 * - a controlled escape hatch via `pinned`, so callers can force the visual
 *   state without disabling the shared API shape
 *
 * In practice this hook is the low-level sticky primitive; higher-level hooks
 * like `useCategoryNavigationShell` supply the thresholds, refs, and release
 * semantics that give the primitive page-specific meaning.
 */
export function useScrollPinning({
  sentinelRef,
  releaseOffsetRef,
  transitionLockRef,
  enabled,
  pinned,
  activateAt,
  releaseAt,
  schedule = 'sync',
}: UseScrollPinningOptions) {
  const [internalPinned, setInternalPinned] = useState(false);

  useEffect(() => {
    if (!enabled || pinned !== undefined) {
      setInternalPinned(false);
      return;
    }

    let frameId: number | null = null;

    const syncPinnedState = () => {
      const sentinel = sentinelRef.current;

      if (!sentinel) {
        return;
      }

      const thresholdTop = sentinel.getBoundingClientRect().top;
      const effectiveReleaseAt = Math.max(
        releaseAt,
        releaseOffsetRef?.current ?? 0,
      );

      setInternalPinned((currentPinned) =>
        transitionLockRef?.current
          ? currentPinned
          : getNextPinnedState(
              currentPinned,
              thresholdTop,
              activateAt,
              effectiveReleaseAt,
            ),
      );
    };

    const updatePinnedState = () => {
      if (schedule !== 'animationFrame') {
        syncPinnedState();
        return;
      }

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        frameId = null;
        syncPinnedState();
      });
    };

    updatePinnedState();
    window.addEventListener('scroll', updatePinnedState, { passive: true });
    window.addEventListener('resize', updatePinnedState);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      window.removeEventListener('scroll', updatePinnedState);
      window.removeEventListener('resize', updatePinnedState);
    };
  }, [
    activateAt,
    enabled,
    pinned,
    releaseAt,
    releaseOffsetRef,
    schedule,
    sentinelRef,
    transitionLockRef,
  ]);

  return enabled && (pinned ?? internalPinned);
}
